/**
 * Hook for managing dark mode state
 * Persists preference to localStorage and applies to document
 *
 * @module hooks/useDarkMode
 */

import { useEffect, useState } from 'react';

/**
 * Hook to manage dark mode state
 *
 * @returns {[boolean, () => void]} Tuple of [isDarkMode, toggleDarkMode]
 */
export function useDarkMode(): [boolean, () => void] {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    // Check localStorage on mount
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) {
      return stored === 'true';
    }
    // Default to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const root = window.document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    // Persist to localStorage
    localStorage.setItem('darkMode', String(isDarkMode));
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  return [isDarkMode, toggleDarkMode];
}
